import {
  Alert,
  Button,
  CircularProgress,
  Stack,
  type AlertColor,
} from "@mui/material";
import { Link } from "@tanstack/react-router";

import {
  exportDisplayName,
  isExportLinkExpired,
  startExportDownload,
} from "./exportDisplay";
import {
  exportTracker,
  useTrackedExportsStore,
  type TrackedExport,
} from "./exportTracker";

const MAX_VISIBLE = 3;

const SEVERITY: Record<TrackedExport["status"], AlertColor> = {
  running: "info",
  ready: "success",
  failed: "error",
};

const alertSx = {
  borderRadius: 0,
  alignItems: "center",
  "& .MuiAlert-action": { pt: 0 },
};

function ViewExportsButton({ siteId }: { siteId: string }) {
  return (
    <Button
      component={Link}
      to="/people/$siteId/audiences/exports"
      params={{ siteId }}
      size="small"
      color="inherit"
      data-testid="export-banner-view-btn"
    >
      View exports
    </Button>
  );
}

function ExportBannerItem({ item }: { item: TrackedExport }) {
  const name = exportDisplayName(item.name, item.kind);
  const { fileUrl, updatedAt } = item;
  const canDownload =
    item.status === "ready" &&
    !!fileUrl &&
    !(updatedAt && isExportLinkExpired(updatedAt));

  const message = {
    running: <>Exporting &ldquo;{name}&rdquo;&hellip;</>,
    ready: <>&ldquo;{name}&rdquo; is ready</>,
    failed: <>Export of &ldquo;{name}&rdquo; failed</>,
  }[item.status];

  return (
    <Alert
      severity={SEVERITY[item.status]}
      data-testid="export-banner-item"
      data-status={item.status}
      icon={
        item.status === "running" ? (
          <CircularProgress size={20} color="inherit" />
        ) : undefined
      }
      sx={alertSx}
      action={
        <Stack direction="row" alignItems="center" gap={1}>
          {canDownload && fileUrl ? (
            <Button
              size="small"
              color="inherit"
              data-testid="export-banner-download-btn"
              onClick={() => {
                startExportDownload(fileUrl, item.name);
              }}
            >
              Download
            </Button>
          ) : null}
          <ViewExportsButton siteId={item.siteId} />
          <Button
            size="small"
            color="inherit"
            data-testid="export-banner-dismiss-btn"
            onClick={() => exportTracker.dismiss(item.jobId)}
          >
            Dismiss
          </Button>
        </Stack>
      }
    >
      {message}
    </Alert>
  );
}

export function ExportBanner() {
  const items = useTrackedExportsStore((s) => s.items);

  if (items.length === 0) return null;

  const newestFirst = [...items].reverse();
  const visible = newestFirst.slice(0, MAX_VISIBLE);
  const hiddenCount = newestFirst.length - visible.length;

  return (
    <Stack data-testid="export-banner">
      {visible.map((item) => (
        <ExportBannerItem key={item.jobId} item={item} />
      ))}
      {hiddenCount > 0 ? (
        <Alert
          severity="info"
          icon={false}
          data-testid="export-banner-more"
          sx={alertSx}
          action={<ViewExportsButton siteId={visible[0].siteId} />}
        >
          +{hiddenCount} more
        </Alert>
      ) : null}
    </Stack>
  );
}
